import type { IntakeSession, IntakeStatusUpdate } from '../intake-client';
import type { AnalysisSetup, ChatReport, ParseSummary } from '../types';
import {
  ChatAnalyzerError,
  createChatAnalyzer,
  INTERNAL_ERROR_MESSAGE,
  isAbortError,
  type ChatAnalyzer,
} from '../worker-client';
import { fileCountParam, messageBucket, roomKindForPeople, trackKrc, type FileSource } from './analytics';
import { defaultAnalysisSetup, type CleanupState } from './cleanup-model';
import { mountCleanupScreen } from './cleanup-screen';
import { fileDropRefusedMessage, INTAKE_EXPIRED_MESSAGE, parseErrorHelp } from './copy';
import { focusScreenHeading, wait, type MountedScreen } from './dom';
import type { UiEnvironment } from './environment';
import { mountErrorScreen, type ErrorScreenOptions } from './error-screen';
import { guardFileDrops } from './file-drop';
import { classifyIntakeError, discardIntakeAddress, intakeErrorMessage, type IntakeApi } from './intake-api';
import type { PollerVisibility } from './intake-poller';
import { createParseProgressTracker, type ProgressStage } from './progress-model';
import { mountProgressScreen, type ProgressScreen } from './progress-screen';
import { mountResultScreen } from './result-screen';
import { mountStartScreen } from './start-screen';
import type { Toast } from './toast';

/**
 * Screen flow for 카톡 성적표: start → reading → name cleanup → result, with
 * the sample going straight to the result and errors offering a retry.
 */

export type ScreenName = 'start' | 'progress' | 'cleanup' | 'result' | 'error';

export interface AppOptions {
  readonly stage: HTMLElement;
  /** Receives data-krc-screen for screen-specific layout. */
  readonly shell: HTMLElement;
  readonly environment: UiEnvironment;
  readonly intake: IntakeApi;
  readonly toast: Toast;
  readonly analyzer?: ChatAnalyzer;
  readonly visibility?: PollerVisibility;
  /** Where the page-wide file drop guard listens; defaults to document (null: none). */
  readonly dropTarget?: EventTarget | null;
}

export interface ReportCardApp {
  /** First render (no focus move). */
  start(): void;
  showStart(): void;
  showCleanup(summary: ParseSummary): void;
  showReport(report: ChatReport, options?: { readonly isSample?: boolean }): void;
  showProgress(stage: ProgressStage, percent: number | null): ProgressScreen;
  showError(options: ErrorScreenOptions): void;
}

/** Keeps "채점하는 중" on screen long enough to read. */
const MIN_ANALYZING_MS = 700;

const REMINDERS: Readonly<Record<FileSource, string>> = {
  file: '대화는 서버로 보내지 않고 이 기기에서만 읽어요.',
  sample: '가상의 샘플 대화예요. 실제 사람의 대화가 아니에요.',
  email: '받은 파일은 이 기기로 옮긴 뒤 서버에서 바로 지워요.',
};

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function createReportCardApp(options: AppOptions): ReportCardApp {
  const analyzer = options.analyzer ?? createChatAnalyzer();
  let screen: MountedScreen | null = null;
  let screenName: ScreenName | null = null;
  let job: AbortController | null = null;
  let summary: ParseSummary | null = null;
  let cleanup: CleanupState | null = null;
  let source: FileSource = 'file';

  // A file dropped outside the start screen must never replace the page (and
  // the report or name cleanup on it) with the raw chat text.
  const dropTarget = options.dropTarget === undefined ? (typeof document === 'undefined' ? null : document) : options.dropTarget;
  if (dropTarget) {
    guardFileDrops(dropTarget, {
      accepts: () => screenName === 'start',
      onRefused: () => options.toast(fileDropRefusedMessage(screenName === 'result')),
    });
  }

  function mount(name: ScreenName, create: (stage: HTMLElement) => MountedScreen, focus = true): MountedScreen {
    screen?.dispose();
    screen = null;
    options.stage.replaceChildren();
    const mounted = create(options.stage);
    screen = mounted;
    screenName = name;
    options.shell.dataset.krcScreen = name;
    if (focus) focusScreenHeading(mounted.heading);
    return mounted;
  }

  function beginJob(): AbortController {
    job?.abort();
    const controller = new AbortController();
    job = controller;
    return controller;
  }

  function mountProgress(stage: ProgressStage, reminder: string, title?: string): ProgressScreen {
    return mount('progress', (target) => mountProgressScreen(target, {
      stage,
      title,
      reminder,
      onCancel: () => job?.abort(),
    })) as ProgressScreen;
  }

  function showStart(focus = true): void {
    job?.abort();
    job = null;
    analyzer.reset();
    summary = null;
    cleanup = null;
    mount('start', (target) => mountStartScreen(target, {
      environment: options.environment,
      intake: options.intake,
      toast: options.toast,
      visibility: options.visibility,
      onFiles: (files) => void parseFiles(files, 'file'),
      onIntakeReceived: (session, status) => void receiveEmail(session, status),
      onSample: () => void startSample(),
    }), focus);
  }

  function showError(errorOptions: ErrorScreenOptions): void {
    mount('error', (target) => mountErrorScreen(target, errorOptions));
  }

  function showFailure(error: unknown): void {
    const code = error instanceof ChatAnalyzerError ? error.code : 'INTERNAL';
    const message = error instanceof ChatAnalyzerError && error.message ? error.message : INTERNAL_ERROR_MESSAGE;
    trackKrc('krc_parse_failed', { code });
    const offerSample = code === 'TOO_FEW_MESSAGES' || code === 'SINGLE_SENDER' || code === 'NOT_KAKAO_EXPORT';
    showError({
      message,
      help: parseErrorHelp(code),
      onRetry: () => showStart(),
      secondary: offerSample ? { label: '샘플로 먼저 보기', onClick: () => void startSample() } : undefined,
    });
  }

  function showCleanup(parsed: ParseSummary): void {
    summary = parsed;
    mount('cleanup', (target) => mountCleanupScreen(target, {
      summary: parsed,
      initialState: cleanup ?? undefined,
      onSubmit: (setup, state) => {
        cleanup = state;
        void analyze(setup);
      },
      onRestart: () => showStart(),
    }));
  }

  function showReport(report: ChatReport, reportOptions: { readonly isSample?: boolean } = {}): void {
    const isSample = reportOptions.isSample ?? source === 'sample';
    mount('result', (target) => mountResultScreen(target, {
      report,
      environment: options.environment,
      toast: options.toast,
      isSample,
      onRestart: () => showStart(),
      onEditNames: () => (summary ? showCleanup(summary) : showStart()),
    }));
  }

  async function analyze(setup: AnalysisSetup, progress?: ProgressScreen): Promise<void> {
    const controller = beginJob();
    const view = progress ?? mountProgress('analyzing', REMINDERS[source]);
    view.setStage('analyzing');
    view.setPercent(null);
    const startedAt = now();
    try {
      const report = await analyzer.analyze(setup, { signal: controller.signal });
      const elapsed = now() - startedAt;
      if (elapsed < MIN_ANALYZING_MS) await wait(MIN_ANALYZING_MS - elapsed);
      if (job !== controller || controller.signal.aborted) {
        if (job === controller) {
          job = null;
          if (summary && source !== 'sample') showCleanup(summary);
          else showStart();
        }
        return;
      }
      job = null;
      showReport(report);
    } catch (error) {
      if (job !== controller) return;
      job = null;
      if (isAbortError(error)) {
        if (summary && source !== 'sample') showCleanup(summary);
        else showStart();
        return;
      }
      showFailure(error);
    }
  }

  async function parseFiles(files: File[], from: FileSource, progress?: ProgressScreen): Promise<void> {
    const controller = beginJob();
    source = from;
    summary = null;
    cleanup = null;
    trackKrc('krc_file_selected', { source: from, file_count: fileCountParam(files.length) });
    const view = progress ?? mountProgress('reading', REMINDERS[from]);
    view.setStage('reading');
    view.setPercent(0);
    const tracker = createParseProgressTracker();
    try {
      const parsed = await analyzer.parse(files, {
        signal: controller.signal,
        onProgress: (value) => {
          if (job !== controller) return;
          view.setStage(value.phase);
          view.setPercent(tracker.update(value));
        },
      });
      if (job !== controller) return;
      view.setPercent(tracker.finish());
      summary = parsed;
      trackKrc('krc_parsed', {
        format: parsed.format,
        room_kind: roomKindForPeople(parsed.participants.length),
        message_bucket: messageBucket(parsed.messageCount),
      });
      job = null;
      if (from === 'sample') await analyze(defaultAnalysisSetup(parsed), view);
      else showCleanup(parsed);
    } catch (error) {
      if (job !== controller) return;
      job = null;
      if (isAbortError(error)) showStart();
      else showFailure(error);
    }
  }

  async function startSample(): Promise<void> {
    const controller = beginJob();
    source = 'sample';
    const view = mountProgress('reading', REMINDERS.sample);
    try {
      const { createSampleChatText, SAMPLE_CHAT_FILE_NAME } = await import('../sample-chat');
      if (job !== controller) return;
      // 그만두기 while the sample chunk was still loading.
      if (controller.signal.aborted) {
        showStart();
        return;
      }
      const file = new File([createSampleChatText()], SAMPLE_CHAT_FILE_NAME, { type: 'text/plain' });
      await parseFiles([file], 'sample', view);
    } catch (error) {
      if (job !== controller) return;
      if (controller.signal.aborted) {
        showStart();
        return;
      }
      job = null;
      console.error(error);
      showError({ message: '샘플 대화를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.', retryLabel: '처음으로', onRetry: () => showStart() });
    }
  }

  async function receiveEmail(session: IntakeSession, status: IntakeStatusUpdate): Promise<void> {
    const controller = beginJob();
    const fileCount = status.files?.length ?? 0;
    trackKrc('krc_intake_received', { file_count: fileCountParam(fileCount) });
    const title = fileCount > 0 ? `메시지 파일 ${fileCount}개가 도착했어요` : undefined;
    const view = mountProgress('downloading', REMINDERS.email, title);
    /** Deletes this address (and only this one) here and on the server; a delete that fails is retried. */
    const forget = () => {
      void options.intake.discard(session);
    };
    // Another open tab took the address over: it downloads and deletes it, not this one.
    let takenOver = false;
    const stopListening = options.intake.onLost((sessionId) => {
      if (sessionId !== session.id) return;
      takenOver = true;
      controller.abort();
    });
    let files: File[];
    try {
      files = await options.intake.download(session, status, controller.signal);
    } catch (error) {
      stopListening();
      if (takenOver) {
        if (job === controller) showStart();
        return;
      }
      const kind = classifyIntakeError(error);
      const aborted = kind === 'aborted' || isAbortError(error);
      if (job !== controller) {
        // Replaced by another job (e.g. a file the person picked): nobody will use the e-mailed chat.
        if (aborted) forget();
        return;
      }
      job = null;
      if (aborted) {
        forget();
        showStart();
        return;
      }
      trackKrc('krc_intake_failed', { reason: kind === 'gone' ? 'EXPIRED' : 'DOWNLOAD_FAILED' });
      if (kind === 'gone') {
        options.intake.clear(session);
        showError({ title: '메일 주소가 만료됐어요', message: INTAKE_EXPIRED_MESSAGE, retryLabel: '처음으로', onRetry: () => showStart() });
        return;
      }
      showError({
        title: '메일로 받은 파일을 가져오지 못했어요',
        message: intakeErrorMessage(kind),
        retryLabel: '다시 받기',
        onRetry: () => void retryEmail(session, status),
        secondary: {
          label: '주소 지우고 처음으로',
          onClick: () => {
            // Marked before the start screen looks for stored addresses; says '지웠어요' only once confirmed.
            void discardIntakeAddress(options.intake, session, options.toast);
            showStart();
          },
        },
      });
      return;
    }
    stopListening();
    if (takenOver) {
      if (job === controller) showStart();
      return;
    }
    // The files are in memory now: delete them from the server right away.
    forget();
    if (job !== controller) return;
    job = null;
    await parseFiles(files, 'email', view);
  }

  /**
   * '다시 받기': another tab may have taken the address over while the error
   * screen was up. Take it back first, so only this tab downloads and deletes it.
   */
  async function retryEmail(session: IntakeSession, status: IntakeStatusUpdate): Promise<void> {
    try {
      await options.intake.take(session);
    } catch {
      // Best effort: the download below still reports its own errors.
    }
    await receiveEmail(session, status);
  }

  return {
    start: () => showStart(false),
    showStart: () => showStart(),
    showCleanup,
    showReport,
    showProgress(stage, percent) {
      const view = mountProgress(stage, REMINDERS.file);
      view.setPercent(percent);
      return view;
    },
    showError,
  };
}
